import styles from "./AppDropdown.module.scss";

import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { AppChevron } from "../AppDatePicker/components/AppChevron";

type Option = { label: string; value: string };

type AppDropdownProps = {
  width?: number;
  marginLeft?: number;
  selectedOption?: Option;
  options: Option[];
  onSelect: (option: Option) => void;
  testId?: string;
  disabled?: boolean;
};

export const AppDropdown = ({
  width,
  marginLeft,
  options,
  selectedOption,
  onSelect,
  testId = "app-dropdown",
  disabled = false,
}: AppDropdownProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (!panelRef.current) {
        return;
      }

      if (!panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", onClickOutside);

    return () => {
      document.removeEventListener("pointerdown", onClickOutside);
    };
  }, [isOpen]);

  const toggle = useCallback(() => {
    if (disabled) {
      return;
    }

    setIsOpen((prev) => !prev);
  }, [disabled]);

  const handleSelect = useCallback(
    (option: Option) => {
      onSelect(option);
      setIsOpen(false);
    },
    [onSelect],
  );

  // Only use this if you need to fine tune the dropdown panel position.
  const cssVars: CSSProperties = {
    "--dropdown-width": width ? `${width}px` : undefined,
    "--dropdown-margin-left": marginLeft ? `${marginLeft}px` : undefined,
  } as CSSProperties;

  const isSelected = useCallback(
    (option: Option) => selectedOption?.value === option.value,
    [selectedOption],
  );

  return (
    <div ref={panelRef} className={styles.dropdownContainer} style={cssVars}>
      <div
        className={`${styles.labelContainer} ${disabled ? styles.disabled : ""}`}
        onClick={toggle}
        data-testid={testId}
      >
        <span className={styles.label}>{selectedOption?.label}</span>
        {!disabled && (
          <AppChevron
            orientation="down"
            size={18}
            disableHover
            tone="primary"
          />
        )}
      </div>
      {isOpen && (
        <div
          className={styles.optionsContainer}
          data-testid={`${testId}-options`}
        >
          <div className={styles.optionsTitle}>
            {t("common.timezones.title")}
          </div>
          {options.map((option) => (
            <span
              key={option.value}
              className={`${styles.option} ${isSelected(option) ? styles.selected : ""}`}
              onClick={() => handleSelect(option)}
            >
              {option.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
